import React, { useMemo } from 'react';
import type { TradeForm } from './types';
import { inputClassName, labelClassName, readOnlyInputClassName, isAmendLocked } from './types';
import { RegistryNotice } from './RegistryNotice';
import { registryNoticeIds } from './registry-notice-ids';
import type { CustodianItem } from '@/types';
import { useCustodians } from '@/hooks';

interface NonCashCollateralSectionProps {
  form: TradeForm;
  isSubmitted: boolean;
  /**
   * Rows for the settlement-place select. MUST be the active custodian registry
   * as `useCustodians` returns it: the empty/failed predicates below pair this
   * length with that query's own loading/error flags, so a list from anywhere
   * else would let the two disagree.
   */
  custodians: CustodianItem[];
  collateralQuantity?: string | number;
  collateralCurrentMarketPrice?: string | number;
  collateralPercent?: string | number;
  /** Booked place's name, so a since-deactivated custodian still shows. */
  collateralSettlementPlaceName?: string | null;
  amendMode?: boolean;
}

const toNumber = (v: string | number | undefined): number => {
  if (v === undefined || v === null || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

export const NonCashCollateralSection: React.FC<NonCashCollateralSectionProps> = ({
  form,
  isSubmitted,
  custodians,
  collateralQuantity,
  collateralCurrentMarketPrice,
  collateralPercent,
  collateralSettlementPlaceName,
  amendMode,
}) => {
  // LR-625: rows keep coming from the prop; only the three-way registry state
  // is read here. Same query key as the form hook's fetch, so it is deduped —
  // no extra request. Loading and failed must not read as "none configured".
  //
  // The two states therefore have different sources — rows from `custodians`,
  // in-flight/failed from the query — which only agrees because every caller
  // passes the list that same query produced (see the prop's docstring). Length
  // is taken from the prop on purpose: it is what the dropdown renders.
  const { isLoading: custodiansLoading, isError: readFailed } = useCustodians();
  // A failed REFRESH over cached rows also sets isError; captioning a populated
  // dropdown with "the registry failed" is the same lie in reverse.
  const custodiansError = readFailed && custodians.length === 0;
  const registryEmpty = !custodiansLoading && !readFailed && custodians.length === 0;
  const collateralValue = useMemo(() => {
    const qty = toNumber(collateralQuantity);
    const price = toNumber(collateralCurrentMarketPrice);
    const pctEmpty =
      collateralPercent === undefined || collateralPercent === null || collateralPercent === '';
    const pct = pctEmpty ? 100 : toNumber(collateralPercent);
    if (!qty || !price) return null;
    return qty * price * (pct / 100);
  }, [collateralQuantity, collateralCurrentMarketPrice, collateralPercent]);

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <form.Field
          name="collateral_isin"
          validators={{
            // LR-718: the section renders only for non-cash, and the gate has to be
            // re-checked here, LIVE - an unmounted field stays registered (empty
            // `FieldApi.mount()` cleanup) and a captured prop freezes on its last
            // mounted render. `validateTradeEntry` has no rule for these two, so a
            // switch back to cash would block Create with nothing on screen at all.
            onChange: ({ value }) => {
              if (form.state.values.collateral_type !== 'non_cash') return undefined;
              if (!value || (typeof value === 'string' && !value.trim())) return 'Collateral ISIN is required';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Collateral ISIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. US9128284V09"
                disabled={isAmendLocked(amendMode, 'collateral_isin')}
                className={inputClassName}
              />
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field
          name="collateral_symbol_id"
          validators={{
            // BO books the collateral securities under this id; the ISIN is not one.
            // Gated live like `collateral_isin`, and off in amend: the field is locked
            // there, and a trade booked before it was required must stay amendable.
            onChange: ({ value }) => {
              if (amendMode || form.state.values.collateral_type !== 'non_cash') return undefined;
              if (!value || (typeof value === 'string' && !value.trim())) return 'Collateral Symbol is required';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Collateral Symbol <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="Backoffice asset id"
                disabled={isAmendLocked(amendMode, 'collateral_symbol_id')}
                className={inputClassName}
              />
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field
          name="collateral_quantity"
          validators={{
            // LR-718: gated live, same reasoning as `collateral_isin` above.
            onChange: ({ value }) => {
              if (form.state.values.collateral_type !== 'non_cash') return undefined;
              if (value === undefined || value === '' || value === null) {
                return 'Collateral quantity is required';
              }
              const n = typeof value === 'string' ? parseFloat(value) : value;
              if (!Number.isFinite(n) || n <= 0) return 'Quantity must be positive';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Collateral Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.000001"
                min="0"
                value={(field.state.value as string | number | undefined) ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                disabled={isAmendLocked(amendMode, 'collateral_quantity')}
                className={inputClassName}
              />
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field name="collateral_percent">
          {(field) => (
            <>
              <label className={labelClassName}>Collateral % (100 = par)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={(field.state.value as string | number | undefined) ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="100.00"
                disabled={isAmendLocked(amendMode, 'collateral_percent')}
                className={inputClassName}
              />
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field name="collateral_cob_price">
          {(field) => (
            <>
              <label className={labelClassName}>COB T-1 Price</label>
              <input
                type="number"
                step="0.000001"
                min="0"
                value={(field.state.value as string | number | undefined) ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                disabled={isAmendLocked(amendMode, 'collateral_cob_price')}
                className={inputClassName}
              />
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field name="collateral_current_market_price">
          {(field) => (
            <>
              <label className={labelClassName}>Current Market Price</label>
              <input
                type="number"
                step="0.000001"
                min="0"
                value={(field.state.value as string | number | undefined) ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                disabled={isAmendLocked(amendMode, 'collateral_current_market_price')}
                className={inputClassName}
              />
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field name="pre_delivery_party">
          {(field) => (
            <>
              <label className={labelClassName}>Pre-Delivery Party</label>
              <select
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value as 'us' | 'counterparty' | 'simultaneous' | '')}
                disabled={isAmendLocked(amendMode, 'pre_delivery_party')}
                className={inputClassName}
              >
                <option value="">— Not specified —</option>
                <option value="us">We deliver first</option>
                <option value="counterparty">Counterparty delivers first</option>
                <option value="simultaneous">Simultaneous (DvD)</option>
              </select>
            </>
          )}
        </form.Field>
      </div>

      <div>
        <form.Field name="collateral_settlement_place">
          {(field) => (
            <>
              <label className={labelClassName} htmlFor="collateral_settlement_place">
                Collateral Settlement Place
              </label>
              <select
                id="collateral_settlement_place"
                value={field.state.value ? String(field.state.value) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.handleChange(v ? Number(v) : null);
                }}
                disabled={isAmendLocked(amendMode, 'collateral_settlement_place')}
                aria-describedby={
                  amendMode
                    ? undefined
                    : registryNoticeIds('collateral_settlement_place', {
                        empty: registryEmpty,
                        error: custodiansError,
                      })
                }
                className={inputClassName}
              >
                {/* See SettlementPlaceSelect: amend states nothing about the
                    register, placeholder included. */}
                <option value="">
                  {registryEmpty && !amendMode ? 'No custodians configured' : 'Select Custodian'}
                </option>
                {/* Booked against a custodian that has since been deactivated:
                    it is absent from the is_active list, so without this the
                    control would read "not set" for a trade that has a place. */}
                {field.state.value != null &&
                  !custodians.some((c) => c.id === field.state.value) && (
                    <option value={String(field.state.value)}>
                      {collateralSettlementPlaceName || `Custodian #${field.state.value}`}
                    </option>
                  )}
                {custodians.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.account_number ? `${c.name} (${c.account_number})` : c.name}
                  </option>
                ))}
              </select>
              {/* Not in amend — the field is locked; see SettlementPlaceSelect. */}
              {!amendMode && (
                <RegistryNotice
                  registry="custodians"
                  idPrefix="collateral_settlement_place"
                  empty={registryEmpty}
                  error={custodiansError}
                />
              )}
            </>
          )}
        </form.Field>
      </div>

      <div className="md:col-span-4">
        {/* LR-474 (C3): explicit currency note, NO conversion — the figure is
            qty × the COLLATERAL security's price, so it is denominated in the
            collateral security's own currency, which can be a THIRD currency
            (neither the trade's security nor its settlement ccy). */}
        <label className={labelClassName}>
          Collateral Value (qty × current price × %, in the collateral security&apos;s currency)
        </label>
        <input
          type="text"
          value={collateralValue !== null ? collateralValue.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'}
          readOnly
          className={readOnlyInputClassName}
        />
      </div>
    </div>
  );
};
